import axios from 'axios';
import { proHydro, crude } from '../config';
// eslint-disable-next-line no-unused-vars
import store from '../store';
// eslint-disable-next-line no-unused-vars
import { logoutUserSuccess } from '../slices/auth/login/reducer';
import { getAuthToken } from '../auth/tokenvalidation';

// Two backends, exported as two clients:
//
//   proHydroApi / proHydroAxios  <- proHydro.API_URL (rha_api)
//     `06.WebApi` (ProHydro): Fungsi, Klausa Category/Information, Jenis
//     Pengadaan, Crude Category, RefineryUnits (the report Plant lookup).
//
//   crudeApi / crudeAxios        <- crude.API_URL (rhacrude_api)
//     `06.WebApi.Crude`: ChangeRequest approval workflow, CDU, Evaluation
//     Document, Reception Facility, Data Tracking, all four Reports.
//     NOTE: ReceptionFacilitiesView.js and DataTrackingView.js briefly used
//     proHydroApi (2026-07-14 merge) before being corrected back to crudeApi —
//     if either 404s, check which client it's actually importing.
//
// Verified via curl 2026-07-14 — these are NOT interchangeable, each 404s on
// the other host. A single screen may legitimately use both (the reports'
// Advance Search reads Plant from ProHydro and everything else from Crude).
// Both share identical auth/error handling, so the axios instance + API client
// are built by a factory instead of duplicating them.
const buildCrudeAxios = baseURL => {
  // axios.create() MERGES the given config with the current global
  // `axios.defaults` — so an undefined/empty baseURL here does not stay empty,
  // it silently inherits whatever `core/apiClient.js` / `helpers/api_helper.js`
  // last set as the global default (the main app API host). That previously
  // sent Crude requests to the wrong host with no error. Fail fast instead.
  if (!baseURL) {
    // eslint-disable-next-line no-console
    console.error(
      'crude_api_helper: missing baseURL — check proHydro.API_URL / ' +
        'crude.API_URL in src/config.js (and, if env-driven, restart the ' +
        'dev server — CRA only reads env files at startup).'
    );
  }
  const instance = axios.create({ baseURL: baseURL || '' });
  // Force the resolved baseURL onto the instance's own defaults so it can
  // never silently inherit the global axios.defaults.baseURL set elsewhere.
  instance.defaults.baseURL = baseURL || '';
  instance.defaults.headers.post['Content-Type'] = 'application/json';

  instance.interceptors.request.use(
    config => {
      const token = getAuthToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    error => Promise.reject(error)
  );

  instance.interceptors.response.use(
    response => (response?.data ? response.data : response),
    error => {
      const status = error?.response?.status;
      let message = 'Terjadi kesalahan.';

      switch (status) {
        case 500:
          // Per api-contract-approval-workflow.md changelog (2026-07-14, #2):
          // state-machine violations used to incorrectly surface as 500 with
          // the real reason in `detail`, making a genuine crash indistinguishable
          // from a business-rule rejection. The API now returns 422 for the
          // latter (see default branch) and reserves 500 for real server
          // faults — whose `detail` may contain internal/stack info and must
          // NOT be shown to the user. Always generic here.
          message = 'Internal Server Error';
          break;
        case 401:
          message = 'Unauthorized - Token tidak valid atau habis';
          // AUTO_LOGOUT_DISABLED_START — disabled per issue #24 (multi-login support), 2026-07-17
          // sessionStorage.clear();
          // store.dispatch(logoutUserSuccess());
          // window.location.href = '/login';
          // return;
          // AUTO_LOGOUT_DISABLED_END
          break;
        case 404:
          message = 'Data tidak ditemukan';
          break;
        default:
          // The API replies with RFC7807 ProblemDetails (`title` / `detail`), not
          // `message` — e.g. 409 duplicate name, 422 business-rule rejection
          // (safe to show verbatim — e.g. "already changed by someone else"),
          // 403 Cancel-by-non-owner. Read those first so the user sees the real
          // reason instead of "Request failed with status code 422".
          message =
            error?.response?.data?.detail ||
            error?.response?.data?.title ||
            error?.response?.data?.message ||
            error.message;
      }

      // 400 ValidationProblemDetails carries a per-field `errors` map (PascalCase
      // DTO keys → string[]) that the plain string message above loses. Attach it
      // to the rejected value so callers can map it onto form fields, WITHOUT
      // changing the existing string contract: a boxed String interpolates and
      // .toString()s exactly like the primitive (`${error}` still yields the
      // message), so the ~35 existing `toast.error(\`...: ${error}\`)` callers are
      // unaffected — they just ignore the extra `.validationErrors` property.
      const fieldErrors = error?.response?.data?.errors;
      if (fieldErrors && typeof fieldErrors === 'object') {
        // eslint-disable-next-line no-new-wrappers
        const boxed = new String(message);
        boxed.validationErrors = fieldErrors;
        return Promise.reject(boxed);
      }

      return Promise.reject(message);
    }
  );

  return instance;
};

// axios v1 strips the Content-Type we set below when the body is a FormData,
// so the browser can add the `boundary` the server needs to parse the parts.
const buildFormData = data => {
  const body = new FormData();
  Object.entries(data || {}).forEach(([key, value]) => {
    if (value !== undefined && value !== null) body.append(key, value);
  });
  return body;
};

class CrudeAPIClient {
  constructor(axiosInstance) {
    this.axios = axiosInstance;
  }

  get(url, params) {
    const entries = params
      ? Object.entries(params).filter(
          ([, val]) => val !== null && val !== undefined && val !== ''
        )
      : [];
    const query = entries.length
      ? '?' +
        entries
          .map(
            ([key, val]) =>
              `${encodeURIComponent(key)}=${encodeURIComponent(val)}`
          )
          .join('&')
      : '';
    return this.axios.get(`${url}${query}`);
  }

  create(url, data) {
    return this.axios.post(url, data);
  }

  // Some Crude endpoints bind with [FromForm] and reject JSON bodies. Send
  // url-encoded form data instead (the instance default is application/json).
  postForm(url, data) {
    const body = new URLSearchParams();
    Object.entries(data || {}).forEach(([key, value]) => {
      if (value !== undefined && value !== null) body.append(key, value);
    });
    return this.axios.post(url, body, {
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    });
  }

  // PUT counterpart of postForm — for [FromForm] update endpoints (e.g. CDU)
  // that expect url-encoded form data with the id inside the body.
  putForm(url, data) {
    const body = new URLSearchParams();
    Object.entries(data || {}).forEach(([key, value]) => {
      if (value !== undefined && value !== null) body.append(key, value);
    });
    return this.axios.put(url, body, {
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    });
  }

  // multipart counterparts of postForm/putForm — for [FromForm] endpoints that
  // also bind an IFormFile (e.g. CrudeDocumentUpload). URLSearchParams cannot
  // carry a File, so these build a real FormData instead. Pass File objects
  // straight through; `undefined`/`null` values are dropped so an optional file
  // (Update) is simply omitted rather than sent as the string "null".
  postMultipart(url, data) {
    return this.axios.post(url, buildFormData(data), {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  }

  putMultipart(url, data) {
    return this.axios.put(url, buildFormData(data), {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  }

  // Raw-FormData counterparts — for endpoints whose multipart body can't be
  // expressed as a flat key→value object: repeated field names (e.g. several
  // `documents` file parts) and/or a custom per-part filename via
  // `FormData.append(field, file, filename)`. The caller builds the FormData
  // itself and passes it through as-is (no `buildFormData` wrapping). Used by
  // Crude AddDetail/UpdateDetail, which match each file to a payload entry by the
  // multipart filename `"{evaluationId}_{fileName}"`.
  postMultipartRaw(url, formData) {
    return this.axios.post(url, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  }

  putMultipartRaw(url, formData) {
    return this.axios.put(url, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  }

  update(url, data) {
    return this.axios.patch(url, data);
  }

  put(url, data) {
    return this.axios.put(url, data);
  }

  delete(url, config = {}) {
    return this.axios.delete(url, { ...config });
  }
}

// ProHydro host (rha_api) — `06.WebApi`.
const proHydroAxios = buildCrudeAxios(proHydro.API_URL);
const proHydroApi = new CrudeAPIClient(proHydroAxios);

// Crude host (rhacrude_api) — `06.WebApi.Crude`.
const crudeAxios = buildCrudeAxios(crude.API_URL);
const crudeApi = new CrudeAPIClient(crudeAxios);

export { CrudeAPIClient, proHydroAxios, proHydroApi, crudeAxios, crudeApi };
